import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Box,
    Typography,
    Autocomplete,
    Chip,
    Stack,
    Divider,
    Grid,
    Card,
    CardContent,
    IconButton
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { searchUsers, getAssignedUsers, getPlans } from "../api/planApi";
import {
    WEEKDAY_CONFIG,
    createEmptyRegistrationSchedule,
    normalizeRegistrationSchedule,
    validateRegistrationSchedule
} from "../utils/planSchedule";

export default function PlanFormModal({ open, onClose, onSave, plan }) {
    const initialFormState = {
        name: "",
        validityDays: "",
        price: "",
        sessions: "",
        additionalData: "",
        paymentDescription: "",
        trainingType: [],
        active: true,
        assignToUser: false,
        assignTo: 0,
        galleryPlans: [],
        isSpecialPrice: false,
        firstBuyOnly: false,
        oneTimeOffer: false,
        exactMonthValidity: false,
        singleCalendarDayValidity: false,
        hasRegistrationSchedule: false,
        registrationSchedule: createEmptyRegistrationSchedule()
    };

    const [form, setForm] = useState(initialFormState);
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const [userSearchValue, setUserSearchValue] = useState("");
    const [scheduleErrors, setScheduleErrors] = useState({});

    // Galerii jaoks
    const [allPlans, setAllPlans] = useState([]);
    const [selectedGalleryPlans, setSelectedGalleryPlans] = useState([]);

    // Function to ensure training types are always in array format
    const parseTrainingType = (trainingType) => {
        if (!trainingType) return [];
        if (Array.isArray(trainingType)) return trainingType;
        if (typeof trainingType === 'string') {
            try {
                const parsed = JSON.parse(trainingType);
                return Array.isArray(parsed) ? parsed : [trainingType];
            } catch (e) {
                return [trainingType];
            }
        }
        return [trainingType];
    };

    // Function to ensure gallery plans are always in array format
    const parseGalleryPlans = (galleryPlans) => {
        if (!galleryPlans) return [];
        if (Array.isArray(galleryPlans)) return galleryPlans;
        return [];
    };

    // Lae kõik plaanid galerii jaoks
    useEffect(() => {
        const loadAllPlans = async () => {
            try {
                const plans = await getPlans();
                setAllPlans(Array.isArray(plans) ? plans : []);
            } catch (error) {
                console.error("Error loading plans:", error);
                setAllPlans([]);
            }
        };

        if (open) {
            loadAllPlans();
        }
    }, [open]);

    useEffect(() => {
        if (plan) {
            const galleryPlans = parseGalleryPlans(plan.galleryPlans);
            const updatedPlan = {
                ...plan,
                trainingType: parseTrainingType(plan.trainingType),
                assignToUser: plan.assignTo === 1,
                assignTo: plan.assignTo || 0,
                galleryPlans: galleryPlans,
                validityDays: plan.exactMonthValidity ? "" : plan.validityDays,
                isSpecialPrice: plan.isSpecialPrice || false,
                firstBuyOnly: plan.firstBuyOnly || false,
                oneTimeOffer: plan.oneTimeOffer || false,
                exactMonthValidity: plan.exactMonthValidity || false,
                singleCalendarDayValidity: plan.singleCalendarDayValidity || false,
                hasRegistrationSchedule: plan.hasRegistrationSchedule || false,
                registrationSchedule: normalizeRegistrationSchedule(plan.registrationSchedule)
            };
            setForm(updatedPlan);
            setSelectedGalleryPlans(galleryPlans);

            if (plan.assignTo === 1 && plan.id) {
                loadAssignedUsers(plan.id);
            } else {
                setSelectedUsers([]);
            }
        } else {
            setForm(initialFormState);
            setSelectedUsers([]);
            setSelectedGalleryPlans([]);
            setUsers([]);
        }
        setScheduleErrors({});
        setUserSearchValue("");
    }, [plan, open]);

    const loadAssignedUsers = async (planId) => {
        try {
            const assignedUsers = await getAssignedUsers(planId);
            setSelectedUsers(Array.isArray(assignedUsers) ? assignedUsers : []);
        } catch (error) {
            console.error("Error loading assigned users:", error);
            setSelectedUsers([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const handleCheckboxChange = (e) => {
        setForm({ ...form, sessions: e.target.checked ? 9999 : "" });
    };

    const handleExactMonthValidityChange = (e) => {
        const isExactMonth = e.target.checked;
        setForm((prev) => ({
            ...prev,
            exactMonthValidity: isExactMonth,
            singleCalendarDayValidity: isExactMonth ? false : prev.singleCalendarDayValidity,
            validityDays: isExactMonth ? "" : prev.validityDays
        }));
    };

    const handleSingleCalendarDayValidityChange = (e) => {
        const isSingleCalendarDay = e.target.checked;
        setForm((prev) => ({
            ...prev,
            singleCalendarDayValidity: isSingleCalendarDay,
            exactMonthValidity: isSingleCalendarDay ? false : prev.exactMonthValidity,
            validityDays: isSingleCalendarDay ? 1 : prev.validityDays
        }));
    };

    const handleSubmit = () => {
        if (form.hasRegistrationSchedule) {
            const { isValid, errors, normalized } = validateRegistrationSchedule(form.registrationSchedule);
            const hasAnyIntervals = Object.values(normalized).some((intervals) => intervals.length > 0);

            if (!isValid || !hasAnyIntervals) {
                setScheduleErrors({
                    ...errors,
                    global: hasAnyIntervals ? null : "Add at least one day/time interval when schedule is enabled."
                });
                return;
            }

            setScheduleErrors({});
        }

        const submitData = {
            ...form,
            validityDays: form.singleCalendarDayValidity
                ? 1
                : form.exactMonthValidity ? (form.validityDays || 30) : form.validityDays,
            assignTo: form.assignToUser ? 1 : 0,
            assignedUsers: form.assignToUser ? selectedUsers : [],
            galleryPlans: selectedGalleryPlans,
            hasRegistrationSchedule: form.hasRegistrationSchedule,
            registrationSchedule: form.hasRegistrationSchedule
                ? normalizeRegistrationSchedule(form.registrationSchedule)
                : null
        };
        onSave(submitData);
    };

    const updateScheduleForDay = (dayKey, updater) => {
        setForm((prev) => {
            const currentSchedule = normalizeRegistrationSchedule(prev.registrationSchedule);
            const nextDayValue = updater(currentSchedule[dayKey] || []);
            return {
                ...prev,
                registrationSchedule: {
                    ...currentSchedule,
                    [dayKey]: nextDayValue
                }
            };
        });
        setScheduleErrors((prev) => ({ ...prev, [dayKey]: null, global: null }));
    };

    const handleScheduleAddInterval = (dayKey) => {
        updateScheduleForDay(dayKey, (dayIntervals) => [...dayIntervals, { start: "09:00", end: "10:00" }]);
    };

    const handleScheduleRemoveInterval = (dayKey, indexToRemove) => {
        updateScheduleForDay(dayKey, (dayIntervals) => dayIntervals.filter((_, index) => index !== indexToRemove));
    };

    const handleScheduleIntervalChange = (dayKey, indexToUpdate, field, value) => {
        updateScheduleForDay(dayKey, (dayIntervals) =>
            dayIntervals.map((interval, index) =>
                index === indexToUpdate ? { ...interval, [field]: value, allDay: false } : interval
            )
        );
    };

    const handleAllDayToggle = (dayKey, checked) => {
        updateScheduleForDay(dayKey, () => (checked ? [{ allDay: true }] : []));
    };

    const handleUserSearch = async (query) => {
        if (query.length >= 3) {
            setUserSearchLoading(true);
            try {
                const results = await searchUsers(query);
                const filteredResults = Array.isArray(results) ? results.filter(user =>
                    !selectedUsers.some(selected => selected.id === user.id)
                ) : [];
                setUsers(filteredResults);
            } catch (error) {
                console.error("Error searching users:", error);
                setUsers([]);
            }
            setUserSearchLoading(false);
        } else {
            setUsers([]);
        }
    };

    const handleUserSelect = (user) => {
        if (user && !selectedUsers.some(selected => selected.id === user.id)) {
            setSelectedUsers([...selectedUsers, user]);
            setUserSearchValue("");
            setUsers([]);
        }
    };

    const handleUserRemove = (userToRemove) => {
        setSelectedUsers(selectedUsers.filter(user => user.id !== userToRemove.id));
    };

    // Galerii planide käsitlemine
    const handleGalleryPlanAdd = (planToAdd) => {
        if (planToAdd.id === plan?.id) return; // Ei saa lisada iseenda
        if (!Array.isArray(selectedGalleryPlans) || !selectedGalleryPlans.some(p => p.id === planToAdd.id)) {
            setSelectedGalleryPlans([...selectedGalleryPlans, planToAdd]);
        }
    };

    const handleGalleryPlanRemove = (planToRemove) => {
        if (Array.isArray(selectedGalleryPlans)) {
            setSelectedGalleryPlans(selectedGalleryPlans.filter(p => p.id !== planToRemove.id));
        }
    };

    // Ensure selectedGalleryPlans is always an array before filtering
    const safeSelectedGalleryPlans = Array.isArray(selectedGalleryPlans) ? selectedGalleryPlans : [];
    const safeAllPlans = Array.isArray(allPlans) ? allPlans : [];

    const availableGalleryPlans = safeAllPlans.filter(p =>
        p.id !== plan?.id &&
        !safeSelectedGalleryPlans.some(selected => selected.id === p.id) &&
        p.active
    );

    const getPlanValidityLabel = (planData) => {
        if (planData.singleCalendarDayValidity) {
            return "1 calendar day";
        }

        return planData.exactMonthValidity ? "1 month" : `${planData.validityDays} days`;
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    {plan ? "Edit Plan" : "Add New Plan"}
                </Typography>
                {plan && (
                    <Box display="flex" alignItems="center" gap={2}>
                        {form.isSpecialPrice && (
                            <Chip
                                label="Special Price"
                                color="error"
                                variant="outlined"
                                size="small"
                            />
                        )}
                        <Typography variant="body2" color={form.active ? 'green' : 'red'}>
                            {form.active ? 'Active' : 'Inactive'}
                        </Typography>
                    </Box>
                )}
            </DialogTitle>
            <DialogContent>
                {/* Basic Plan Information */}
                <Card variant="outlined" sx={{ mb: 3, mt: 1 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Basic Information
                        </Typography>
                        
                        <TextField
                            select
                            label="Training Types"
                            name="trainingType"
                            fullWidth
                            margin="normal"
                            value={form.trainingType || []}
                            onChange={handleChange}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => selected.join(', ')
                            }}
                            helperText="Select one or more training types"
                        >
                            <MenuItem value="All classes">ALL CLASSES</MenuItem>
                            <MenuItem value="WOD">WOD</MenuItem>
                            <MenuItem value="Weightlifting">Weightlifting</MenuItem>
                            <MenuItem value="Cardio">Cardio</MenuItem>
                            <MenuItem value="Rowing">Rowing</MenuItem>
                            <MenuItem value="Gymnastics">Gymnastics</MenuItem>
                            <MenuItem value="Open Gym">Open Gym</MenuItem>
                            <MenuItem value="Kids">Kids</MenuItem>
                            <MenuItem value="Basic">Basic</MenuItem>
                            <MenuItem value="Canoeing">Canoeing</MenuItem>
                            <MenuItem value="Hyrox">Hyrox</MenuItem>
                            <MenuItem value="Circuit">Circuit</MenuItem>
                            <MenuItem value="Strength">Strength</MenuItem>
                            <MenuItem value="Hybrid">Hybrid</MenuItem>
                            <MenuItem value="Mobility">Mobility</MenuItem>
                        </TextField>

                        <TextField
                            label="Plan Name"
                            name="name"
                            fullWidth
                            margin="normal"
                            value={form.name}
                            onChange={handleChange}
                            required
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label={
                                        form.singleCalendarDayValidity
                                            ? "Validity Period (1 Calendar Day)"
                                            : form.exactMonthValidity ? "Validity Period (1 Month)" : "Validity Period (Days)"
                                    }
                                    name="validityDays"
                                    type="number"
                                    fullWidth
                                    margin="normal"
                                    value={form.validityDays}
                                    onChange={handleChange}
                                    disabled={form.exactMonthValidity || form.singleCalendarDayValidity}
                                    helperText={
                                        form.singleCalendarDayValidity
                                            ? "This plan is valid for the purchase calendar day only, not 24 hours."
                                            : form.exactMonthValidity ? "This plan will always add exactly one calendar month." : ""
                                    }
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Price (€)"
                                    name="price"
                                    type="number"
                                    step="0.01"
                                    fullWidth
                                    margin="normal"
                                    value={form.price}
                                    onChange={handleChange}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Number of Sessions"
                                    name="sessions"
                                    type="number"
                                    fullWidth
                                    margin="normal"
                                    value={form.sessions}
                                    onChange={handleChange}
                                    disabled={form.sessions === 9999}
                                    required
                                />
                            </Grid>
                        </Grid>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.sessions === 9999}
                                    onChange={handleCheckboxChange}
                                />
                            }
                            label="Unlimited Sessions"
                            sx={{ mt: 1 }}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.exactMonthValidity}
                                    onChange={handleExactMonthValidityChange}
                                />
                            }
                            label="One month validity (calendar month)"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.singleCalendarDayValidity}
                                    onChange={handleSingleCalendarDayValidityChange}
                                />
                            }
                            label="Valid for one calendar day only (not 24h)"
                        />

                        <TextField
                            label="Additional Information"
                            name="additionalData"
                            multiline
                            rows={3}
                            fullWidth
                            margin="normal"
                            value={form.additionalData}
                            onChange={handleChange}
                        />

                        <TextField
                            label="Payment Description (shows on invoice)"
                            name="paymentDescription"
                            multiline
                            rows={2}
                            fullWidth
                            margin="normal"
                            value={form.paymentDescription || ""}
                            onChange={handleChange}
                            helperText="Custom description to appear on the payment invoice (e.g., 'Monthly membership - CrossFit')"
                        />
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Registration Schedule
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.hasRegistrationSchedule}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            hasRegistrationSchedule: e.target.checked,
                                            registrationSchedule: e.target.checked
                                                ? normalizeRegistrationSchedule(prev.registrationSchedule)
                                                : createEmptyRegistrationSchedule()
                                        }))
                                    }
                                />
                            }
                            label="Enable weekly registration schedule"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Set allowed class registration windows by weekday. You can add multiple intervals per day or mark a day as all day.
                        </Typography>

                        {form.hasRegistrationSchedule && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {WEEKDAY_CONFIG.map((day) => {
                                    const dayIntervals = normalizeRegistrationSchedule(form.registrationSchedule)[day.key] || [];
                                    const isAllDay = dayIntervals.some((interval) => interval.allDay);
                                    return (
                                        <Box key={day.key} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2 }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    {day.label}
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <FormControlLabel
                                                        sx={{ m: 0 }}
                                                        control={
                                                            <Checkbox
                                                                checked={isAllDay}
                                                                onChange={(e) => handleAllDayToggle(day.key, e.target.checked)}
                                                            />
                                                        }
                                                        label="All day"
                                                    />
                                                    <Button
                                                        size="small"
                                                        startIcon={<AddCircleOutlineIcon />}
                                                        onClick={() => handleScheduleAddInterval(day.key)}
                                                        disabled={isAllDay}
                                                    >
                                                        Add interval
                                                    </Button>
                                                </Box>
                                            </Box>

                                            {!isAllDay && dayIntervals.length > 0 && (
                                                <Stack spacing={1} sx={{ mt: 1 }}>
                                                    {dayIntervals.map((interval, index) => (
                                                        <Box key={`${day.key}-${index}`} sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                                            <TextField
                                                                type="time"
                                                                size="small"
                                                                label="Start"
                                                                value={interval.start || ""}
                                                                onChange={(e) =>
                                                                    handleScheduleIntervalChange(day.key, index, "start", e.target.value)
                                                                }
                                                                InputLabelProps={{ shrink: true }}
                                                            />
                                                            <TextField
                                                                type="time"
                                                                size="small"
                                                                label="End"
                                                                value={interval.end || ""}
                                                                onChange={(e) =>
                                                                    handleScheduleIntervalChange(day.key, index, "end", e.target.value)
                                                                }
                                                                InputLabelProps={{ shrink: true }}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleScheduleRemoveInterval(day.key, index)}
                                                            >
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            )}

                                            {scheduleErrors[day.key] && (
                                                <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                                                    {scheduleErrors[day.key]}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                                {scheduleErrors.global && (
                                    <Typography variant="body2" color="error">
                                        {scheduleErrors.global}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Special Options */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Special Options
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.isSpecialPrice}
                                        onChange={(e) => setForm({ ...form, isSpecialPrice: e.target.checked })}
                                        color="error"
                                    />
                                }
                                label="Mark as Special Price"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.firstBuyOnly}
                                        onChange={(e) => setForm({ ...form, firstBuyOnly: e.target.checked })}
                                        color="error"
                                    />
                                }
                                label="Plan is visible only for first-time buyers"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.oneTimeOffer}
                                        onChange={(e) => setForm({ ...form, oneTimeOffer: e.target.checked })}
                                        color="error"
                                    />
                                }
                                label="User can purchase this plan only once"
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* User Assignment */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            User Assignment
                        </Typography>
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.assignToUser}
                                    onChange={(e) => {
                                        setForm({ ...form, assignToUser: e.target.checked });
                                        if (!e.target.checked) {
                                            setSelectedUsers([]);
                                            setUsers([]);
                                            setUserSearchValue("");
                                        }
                                    }}
                                />
                            }
                            label="Assign to Specific Users"
                        />

                        {form.assignToUser && (
                            <Box sx={{ mt: 2 }}>
                                <Autocomplete
                                    options={users}
                                    getOptionLabel={(option) => `${option.fullName || 'No name'} (${option.email})`}
                                    value={null}
                                    inputValue={userSearchValue}
                                    onChange={(event, newValue) => {
                                        if (newValue) {
                                            handleUserSelect(newValue);
                                        }
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        setUserSearchValue(newInputValue);
                                        handleUserSearch(newInputValue);
                                    }}
                                    loading={userSearchLoading}
                                    noOptionsText="Type at least 3 characters to search..."
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search and Add Users"
                                            placeholder="Search by name or email..."
                                            helperText="Type at least 3 characters to search users"
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Box>
                                                <Typography variant="body1">
                                                    {option.fullName || 'No name'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {option.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                />

                                {selectedUsers.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Selected Users ({selectedUsers.length}):
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {selectedUsers.map((user) => (
                                                <Chip
                                                    key={user.id}
                                                    label={`${user.fullName || 'No name'} (${user.email})`}
                                                    onDelete={() => handleUserRemove(user)}
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ mb: 1 }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Plan Gallery */}
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Plan Gallery
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                            Add other plans to display as a gallery carousel with this plan
                        </Typography>

                        {availableGalleryPlans.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Available Plans:
                                </Typography>
                                <Grid container spacing={2}>
                                    {availableGalleryPlans.map((availablePlan) => (
                                        <Grid item xs={12} sm={6} md={4} key={availablePlan.id}>
                                            <Card
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        boxShadow: 3,
                                                        transform: 'translateY(-2px)'
                                                    },
                                                    transition: 'all 0.2s'
                                                }}
                                                onClick={() => handleGalleryPlanAdd(availablePlan)}
                                            >
                                                <CardContent sx={{ p: 2 }}>
                                                    <Typography variant="subtitle1" fontWeight="medium">
                                                        {availablePlan.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        €{availablePlan.price} • {getPlanValidityLabel(availablePlan)}
                                                    </Typography>
                                                    {availablePlan.isSpecialPrice && (
                                                        <Chip
                                                            label="Special Price"
                                                            color="error"
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{ mt: 1 }}
                                                        />
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}

                        {safeSelectedGalleryPlans.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Selected Gallery Plans ({safeSelectedGalleryPlans.length}):
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {safeSelectedGalleryPlans.map((galleryPlan) => (
                                        <Chip
                                            key={galleryPlan.id}
                                            label={galleryPlan.name}
                                            onDelete={() => handleGalleryPlanRemove(galleryPlan)}
                                            color="secondary"
                                            variant="outlined"
                                            sx={{ mb: 1 }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Plan Status */}
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                            Plan Status
                        </Typography>
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.active}
                                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                    color="success"
                                />
                            }
                            label="Active"
                        />
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                {plan && (
                    <Button
                        color="error"
                        onClick={() => onSave(null)}
                        sx={{ mr: 'auto' }}
                    >
                        Delete
                    </Button>
                )}
                <Button onClick={onClose} color="secondary">Close</Button>
                <Button
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={
                        !form.name ||
                        (!form.exactMonthValidity && !form.singleCalendarDayValidity && !form.validityDays) ||
                        form.price === "" ||
                        (form.sessions === "" && form.sessions !== 9999) ||
                        (form.assignToUser && selectedUsers.length === 0)
                    }
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}